import { omit } from 'lodash';
import { API_GATEWAY_URL } from '../constants/config';
import { withStandardHandlers } from './utils';
const PROJECTS_API_URL = `${API_GATEWAY_URL}/projects`;
export const GET_PROJECTS_API_URL = (...args) => `${PROJECTS_API_URL}/${args.join('/')}`;
/**
 * Get all projects
 * @param accessToken - The access token for authentication.
 * @param userId - Optional user ID to filter projects.
 */
export const getProjects = async (accessToken, userId) => {
    const doFetch = async (allProjects, lastEvaluatedKey) => {
        const params = new URLSearchParams({ limit: '1000' });
        if (userId) {
            params.append('user_id', userId);
        }
        if (lastEvaluatedKey) {
            params.append('last_evaluated_key', JSON.stringify(lastEvaluatedKey));
        }
        const fn = () => {
            return fetch(`${PROJECTS_API_URL}?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    responseType: 'json',
                    'X-Authorization': `Bearer ${accessToken}`,
                },
            });
        };
        const response = await withStandardHandlers(fn, 'errors.project.fetch', false)();
        allProjects.push(...response.projects);
        if (response.last_evaluated_key) {
            return doFetch(allProjects, response.last_evaluated_key);
        }
        return allProjects;
    };
    return doFetch([]);
};
/**
 * Get a project by ID
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 */
export const getProject = (accessToken, projectId) => {
    const fn = () => fetch(GET_PROJECTS_API_URL(projectId), {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
    });
    return withStandardHandlers(fn, 'errors.project.fetch')();
};
/**
 * Get shared projects
 * @param accessToken - The access token for authentication.
 * @param userId - Optional user ID to filter shared projects.
 */
export const getInvitedProjects = async (accessToken, userId) => {
    const params = new URLSearchParams({ shared: 'true' });
    if (userId) {
        params.append('user_id', userId);
    }
    const fn = () => fetch(`${PROJECTS_API_URL}?${params.toString()}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
    });
    // The gateway wraps the list under `projects` (same shape as `getProjects`).
    const response = await withStandardHandlers(fn, 'errors.project.fetch')();
    return response.projects ?? [];
};
/**
 * Create a new project
 * @param accessToken - The access token for authentication.
 * @param project - The project details to create.
 */
export const postProject = (accessToken, project) => {
    const fn = () => fetch(`${PROJECTS_API_URL}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(omit(project, ['user_id', 'project_id', 'created_at', 'updated_at', 'stats'])),
    });
    // The gateway wraps the created project under `project`.
    return withStandardHandlers(fn, 'errors.project.create')().then((response) => response.project);
};
/**
 * Update an existing project
 * @param accessToken - The access token for authentication.
 * @param projectId - The ID of the project to update.
 * @param project - The project details to update.
 */
export const patchProject = (accessToken, projectId, project) => {
    const fn = () => fetch(GET_PROJECTS_API_URL(projectId), {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(omit(project, ['user_id', 'project_id', 'created_at', 'updated_at', 'stats'])),
    });
    return withStandardHandlers(fn, 'errors.project.update')();
};
/**
 * Invite a user to a project
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param emailAddress - The email address of the user to invite.
 */
export const inviteUserToProject = (accessToken, projectId, emailAddress) => {
    const fn = () => fetch(`${API_GATEWAY_URL}/invite-user`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
            project_id: projectId,
            email_address: emailAddress,
        }),
    });
    return withStandardHandlers(fn, 'errors.project.inviteUser')();
};
/**
 * Remove an invited user from a project
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID.
 * @param userId - The user ID to remove.
 */
export const deleteInvitedUserFromProject = (accessToken, projectId, userId) => {
    const fn = () => fetch(GET_PROJECTS_API_URL(projectId, 'users', userId), {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
    });
    return withStandardHandlers(fn, 'errors.project.removeUser')();
};
/**
 * Delete a project
 * @param accessToken - The access token for authentication.
 * @param projectId - The project ID to delete.
 */
export const deleteProject = (accessToken, projectId) => {
    const fn = () => fetch(GET_PROJECTS_API_URL(projectId), {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
    });
    return withStandardHandlers(fn, 'errors.project.delete')();
};
